<ng-container *transloco="let t">
  <div class="justify-content-space-between">
    <form [formGroup]="form" style="margin-right: 24px">
      <div
        class="relative rounded-md shadow-sm"
        style="margin-bottom: 22px; width: 65%"
      >
        <div
          class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
        >
          <span class="search-icon"></span>
        </div>

        <input
          formControlName="search"
          type="text"
          name="name"
          id="name"
          class="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary focus:outline-none sm:text-sm sm:leading-6"
          [placeholder]="t('Search by document name...')"
        />

        @if (form.controls.search.value?.length) {
          <button
            matIconSuffix
            class="absolute inset-y-0 right-0 flex items-center pr-3"
            (click)="clearSearch()"
          >
            <mat-icon
              class="close-icon icon-xs cursor"
              style="width: 16px; height: 16px"
            ></mat-icon>
          </button>
        }
      </div>
      <div style="width: 35%; display: flex; gap: 16px; align-content: center">
        <mat-form-field appearance="outline" style="width: 100%">
          <mat-date-range-input [formGroup]="range" [rangePicker]="picker">
            <input
              matStartDate
              formControlName="start"
              [placeholder]="t('Start date')"
            />
            <input
              matEndDate
              formControlName="end"
              [placeholder]="t('End date')"
            />
          </mat-date-range-input>

          <mat-date-range-picker #picker></mat-date-range-picker>

          @if (range.controls.start.hasError('matStartDateInvalid')) {
            <mat-error>{{ t('Invalid start date') }}</mat-error>
          }
          @if (range.controls.end.hasError('matEndDateInvalid')) {
            <mat-error>{{ t('Invalid end date') }}</mat-error>
          }
          <div style="display: flex; gap: 12px">
            @if (range.value.start && range.value.end) {
              <button
                matIconSuffix
                mat-icon-button
                (click)="clearRange()"
                aria-label="Clear date range"
              >
                <mat-icon
                  class="close-icon icon-xs cursor"
                  style="width: 16px; height: 16px"
                ></mat-icon>
              </button>
            }

            <mat-datepicker-toggle
              matIconSuffix
              [for]="picker"
            ></mat-datepicker-toggle>
          </div>
        </mat-form-field>
      </div>
    </form>

    @if (!exitusLetalis) {
      <div style="height: 60px">
        <button mat-button (click)="addDocument()" class="primary-btn">
          <mat-icon class="plus-icon"></mat-icon>
          {{ t('Add Document') }}
        </button>
      </div>
    }
  </div>

  <div class="mx-4 sm:-mx-0">
    <div class="overflow-hidden sm:rounded-lg">
      <div
        #tableWrapper
        class="table-wrapper table-wrapper--mobile-cards"
        style="position: relative"
        infiniteScroll
        [infiniteScrollDistance]="2"
        [infiniteScrollThrottle]="100"
        [scrollWindow]="false"
        (scrolled)="loadMore()"
      >
        <anihis-loading-overlay
          [isLoading]="store.statusSearch() === status.Loading"
          [size]="50"
        />

        @defer (when store.status() === status.Loaded) {
          @if (store.data()?.length! > 0) {
            <div class="mobile-card-list">
              @for (property of store.data(); track property.uid) {
                <anihis-admin-item-mobile-card
                  [title]="property.documentName || property.fileName || '—'"
                  [fields]="[
                    {
                      label: t('Document uploaded by'),
                      value: property.uploadedBy,
                    },
                    {
                      label: t('Document clinic name'),
                      value: property.createdBy,
                    },
                    {
                      label: t('Document created date'),
                      value: property.createdDateTimeUtc | localDate,
                    },
                  ]"
                  [showDelete]="false"
                  [showEdit]="false"
                  [showPreview]="true"
                  (preview)="previewDocument(property)"
                />
              }
            </div>

            <mat-table class="min-w-max desktop-only-table" [dataSource]="store.data()!">
              <!-- Owner Column -->
              <ng-container matColumnDef="Name of Document" sticky>
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header"
                  mat-header-cell
                  [matTooltip]="t('Document name')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Document name') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1"
                >
                  <mat-chip class="chip-grey">
                    <div class="flex gap-s">
                      <span
                        class="truncate max-w-[220px] block"
                        [matTooltip]="
                          property.documentName || property.fileName
                        "
                        matTooltipShowDelay="200"
                        matTooltipPosition="above"
                        >{{ property.documentName || property.fileName }}</span
                      >
                    </div>
                  </mat-chip>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="Created By">
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header"
                  mat-header-cell
                  [matTooltip]="t('Document uploaded by')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Document uploaded by') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1"
                >
                  <span
                    [matTooltip]="property.uploadedBy"
                    matTooltipShowDelay="200"
                    matTooltipPosition="above"
                    [innerHTML]="
                      property.uploadedBy | limitTo: 30 | emptyFallbackHtml
                    "
                  ></span>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="Name Clinic">
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header"
                  mat-header-cell
                  [matTooltip]="t('Document clinic name')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Document clinic name') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1"
                >
                  <span
                    [matTooltip]="property.createdBy"
                    matTooltipShowDelay="200"
                    matTooltipPosition="above"
                    [innerHTML]="property.createdBy | emptyFallbackHtml"
                  ></span>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="Created Date">
                <mat-header-cell
                  *matHeaderCellDef
                  class="custom-header"
                  mat-header-cell
                  [matTooltip]="t('Document created date')"
                  matTooltipShowDelay="400"
                >
                  <span>{{ t('Document created date') }}</span>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property"
                  mat-cell
                  class="flex flex-row gap-1"
                >
                  <span
                    [matTooltip]="
                      property.createdDateTimeUtc | localDate
                    "
                    matTooltipShowDelay="200"
                    matTooltipPosition="above"
                    [innerHTML]="
                      property.createdDateTimeUtc
                        | localDate
                        | emptyFallbackHtml
                    "
                  ></span>
                </mat-cell>
              </ng-container>

              <ng-container matColumnDef="Actions" stickyEnd>
                <mat-header-cell *matHeaderCellDef mat-header-cell>
                </mat-header-cell>
                <mat-cell
                  *matCellDef="let property; let i = index"
                  mat-cell
                  class="flex flex-row gap-1"
                  [ngClass]="{
                    'sticky-actions-last': i === (store.data()?.length ?? 0) - 1
                  }"
                >
                  <div class="flex gap-3 justify-center">
                    <button
                      [matTooltip]="t('Preview document')"
                      matTooltipShowDelay="200"
                      matTooltipPosition="above"
                      (click)="previewDocument(property)"
                      class="flex items-center justify-center"
                      [attr.aria-label]="t('Preview document')"
                    >
                      <mat-icon class="eye-icon icon-s"></mat-icon>
                    </button>
                  </div>
                </mat-cell>
              </ng-container>

              <!-- Row Definitions -->
              <mat-header-row
                *matHeaderRowDef="displayedColumns; sticky: true"
              ></mat-header-row>

              <mat-row
                *matRowDef="let row; columns: displayedColumns"
              ></mat-row>
            </mat-table>

            @if (
              store.status() === status.Loading &&
              store.statusSearch() !== status.Loading
            ) {
              <div class="centered-message mt-4">
                <anihis-loading-spinner
                  [size]="'small'"
                ></anihis-loading-spinner>
              </div>
            }
          } @else {
            <div class="centered-message mt-4">
              {{ t('You have no documents') }}
            </div>
          }
        } @placeholder {
          <div class="scroll-hidden">
            <anihis-loading-skeleton [height]="'32dvh'" [minWidth]="'100%'" />
          </div>
        }
      </div>
    </div>
  </div>
</ng-container>

@if (isPreviewing()) {
  <div class="print-overlay">
    <anihis-loading-spinner [size]="'medium'"></anihis-loading-spinner>
  </div>
}
